import { Injectable, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ConnectivityService } from '../connectivity/connectivity.service';
import { OfflineValidationService } from './offline-validation.service';
import { SaleValidateService, TicketValidationData } from '../sale-validate/sale-validate.service';
import { ConflictLogPayload, ConflictType, PendingValidation } from './types';

/**
 * C2-FRO-002 Fase 4 — Orquestador de sincronización de la cola offline.
 *
 * Responsabilidades:
 *   1. Drenar `pending_validations` cuando hay conexión.
 *   2. Replay del `markTicketInUse` server-side con el mismo `idempotency_key`
 *      generado al enqueue → C2-SEC-002 (UPDATE atómico AND is_used=0) garantiza
 *      que un segundo replay del mismo item NO produce doble validación.
 *   3. Backoff exponencial (1s, 2s, 4s, ... cap 60s, max 10 retries) sobre 5xx /
 *      network errors.
 *   4. 409 cross-puerta → loggear conflict + marcar item como `conflict`.
 *   5. 4xx no-409 → marcar item como `failed` (no retry; estado terminal).
 *
 * Mutex:
 *   Un flag local `isProcessing` evita que dos triggers concurrentes (ej:
 *   evento online + tap manual al botón "Reintentar") procesen el mismo row
 *   dos veces. NO es defensivo contra múltiples tabs — IndexedDB es por-origen
 *   pero el flag es por-instancia. Si en el futuro se abren múltiples tabs
 *   simultáneas validando, requiere upgrade a Web Locks API.
 *
 * Triggers:
 *   - online: subscripción a `ConnectivityService.isOnline$` (emite cuando
 *     vuelve la conexión).
 *   - manual: método público `triggerSync()` que llaman el botón del banner
 *     y el bootstrap de AppComponent.
 */

const BACKOFF_INITIAL_MS = 1_000;
const BACKOFF_CAP_MS = 60_000;
const MAX_RETRIES = 10;

@Injectable({ providedIn: 'root' })
export class SyncOrchestratorService implements OnDestroy {
  private isProcessing = false;
  private readonly destroy$ = new Subject<void>();
  private clientDeviceId: string | null = null;

  constructor(
    private readonly connectivity: ConnectivityService,
    private readonly offlineValidation: OfflineValidationService,
    private readonly saleValidateService: SaleValidateService,
  ) {}

  /**
   * Inicialización tardía — debe llamarse una sola vez desde AppComponent.
   * No usamos auto-init en constructor porque queremos control explícito del
   * lifecycle (poder testear sin auto-subscripciones).
   */
  initialize(): void {
    this.connectivity.isOnline$
      .pipe(takeUntil(this.destroy$))
      .subscribe((online) => {
        if (online) {
          // Fire-and-forget; los errores los maneja el processQueue interno.
          void this.triggerSync();
        }
      });
    // Intento inicial: si arrancamos con cola pendiente y conexión, drenar.
    void this.triggerSync();
  }

  /**
   * Trigger público (botón "Reintentar" del banner, bootstrap, evento online).
   * Idempotente: si ya está procesando, retorna inmediatamente.
   */
  async triggerSync(): Promise<void> {
    if (this.isProcessing) return;
    if (!this.connectivity.getSnapshot()) return;
    this.isProcessing = true;
    try {
      await this.processQueue();
    } finally {
      this.isProcessing = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─────────────────────────────────────────────────────────────
  // Internals
  // ─────────────────────────────────────────────────────────────

  /**
   * Drena la cola FIFO un row a la vez. Si se pierde conexión mid-flow,
   * abandona en silencio — el próximo trigger online la retomará.
   */
  private async processQueue(): Promise<void> {
    const pending = await this.offlineValidation.getPendingQueue();
    for (const item of pending) {
      if (!this.connectivity.getSnapshot()) break;
      if (item.id === undefined) continue;
      await this.processItem(item);
    }
  }

  private async processItem(item: PendingValidation): Promise<void> {
    const id = item.id!;
    await this.offlineValidation.markStatus(id, 'syncing');
    const data: TicketValidationData = {
      ticket_code: item.ticket_code,
      porter_code: item.porter_code,
      all_marked: item.all_marked,
    };
    try {
      await this.saleValidateService
        .markTicketInUse(data, item.idempotency_key)
        .toPromise();
      await this.offlineValidation.markValidationSynced(id);
    } catch (err) {
      await this.handleError(item, err);
    }
  }

  private async handleError(item: PendingValidation, err: unknown): Promise<void> {
    const id = item.id!;
    const status = err instanceof HttpErrorResponse ? err.status : 0;

    // 409 — cross-puerta. Loggear conflict-log + marcar item como conflict.
    if (status === 409) {
      const conflictType = this.inferConflictType(err as HttpErrorResponse);
      const payload: ConflictLogPayload = {
        function_id: item.function_id,
        ticket_code: item.ticket_code,
        conflict_type: conflictType,
        porter_code: item.porter_code,
        client_scanned_at: new Date(item.created_at).toISOString(),
        client_device_id: this.getDeviceId(),
        notes: `Offline replay, retry #${item.retry_count}`,
      };
      try {
        await this.saleValidateService.logConflict(payload).toPromise();
      } catch (logErr) {
        // El conflict-log no es crítico para el flow — si falla, lo dejamos
        // pasar y al menos marcamos el item local como conflict.
        console.warn('[C2-FRO-002] logConflict failed:', logErr);
      }
      await this.offlineValidation.markValidationConflict(id, this.serializeError(err));
      return;
    }

    // 4xx no-409 — estado terminal (ticket inválido, porter malo, payload roto).
    if (status >= 400 && status < 500) {
      await this.offlineValidation.markValidationFailedPermanent(
        id,
        this.serializeError(err),
      );
      return;
    }

    // 5xx o network (status 0): retry con backoff exponencial.
    const nextRetryCount = (item.retry_count ?? 0) + 1;
    if (nextRetryCount > MAX_RETRIES) {
      await this.offlineValidation.markValidationFailedMaxRetries(
        id,
        this.serializeError(err),
      );
      return;
    }
    const backoffMs = Math.min(
      BACKOFF_INITIAL_MS * Math.pow(2, item.retry_count ?? 0),
      BACKOFF_CAP_MS,
    );
    const nextRetryAt = Date.now() + backoffMs;
    await this.offlineValidation.markValidationFailed(
      id,
      this.serializeError(err),
      nextRetryAt,
    );
  }

  /**
   * Heurística simple para mapear el error 409 a un ConflictType reconocido por
   * el backend (CHECK SQL). El backend valida y normaliza si llega un tipo no
   * esperado; aquí solo mejoramos el grep posterior.
   */
  private inferConflictType(err: HttpErrorResponse): ConflictType {
    const body = (err.error ?? {}) as { code?: string; message?: string };
    const blob = `${body.code ?? ''} ${body.message ?? ''}`.toLowerCase();
    if (blob.includes('already used') || blob.includes('isused')) {
      return 'ticket_already_used_by_other_door';
    }
    if (blob.includes('not found')) return 'ticket_not_found';
    if (blob.includes('porter')) return 'invalid_porter_code';
    if (blob.includes('inactive')) return 'ticket_inactive';
    return 'unknown';
  }

  private serializeError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      return `HTTP ${err.status}: ${err.message ?? ''}`;
    }
    if (err instanceof Error) return err.message;
    try {
      return JSON.stringify(err);
    } catch {
      return String(err);
    }
  }

  /**
   * Device-id persistente por origin (sobrevive a reloads, no entre dominios).
   * Útil para auditoría de conflict-log: identifica qué dispositivo encoló
   * cada validación. NO usar para tracking — es local-only.
   */
  private getDeviceId(): string {
    if (this.clientDeviceId) return this.clientDeviceId;
    const KEY = 'goliiive_validation_device_id';
    try {
      const existing = localStorage.getItem(KEY);
      if (existing) {
        this.clientDeviceId = existing;
        return existing;
      }
      const fresh =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(KEY, fresh);
      this.clientDeviceId = fresh;
      return fresh;
    } catch {
      // localStorage bloqueado (private mode): degrada a in-memory.
      this.clientDeviceId = `mem-${Math.random().toString(36).slice(2, 10)}`;
      return this.clientDeviceId;
    }
  }
}
